/**
 * Utilities for cascading dashboard filter dependencies.
 * Parent → child chains: province_ids → facility_ids,
 * commodity_classification_ids → item_category_ids → item_ids.
 */

export const buildDependencyChildrenMap = (definitions = []) => {
  const children = {}

  definitions.forEach(definition => {
    ;(definition.depends_on || []).forEach(parentKey => {
      if (!children[parentKey]) {
        children[parentKey] = []
      }
      children[parentKey].push(definition.key)
    })
  })

  return children
}

export const getDescendantFilterKeys = (parentKey, definitions = []) => {
  const childrenMap = buildDependencyChildrenMap(definitions)
  const descendants = new Set()
  const queue = [...(childrenMap[parentKey] || [])]

  while (queue.length > 0) {
    const key = queue.shift()
    if (descendants.has(key)) {
      continue
    }
    descendants.add(key)
    ;(childrenMap[key] || []).forEach(childKey => queue.push(childKey))
  }

  return descendants
}

export const clearDescendantFilters = (filters, changedKey, definitions = []) => {
  const nextFilters = { ...filters }
  const descendants = getDescendantFilterKeys(changedKey, definitions)

  descendants.forEach(key => {
    delete nextFilters[key]
  })

  return nextFilters
}

export const pruneInvalidFilterSelections = (filters, definitions = [], options = {}) => {
  let changed = false
  const nextFilters = { ...filters }

  definitions.forEach(definition => {
    if (definition.type !== "multi_select" || !definition.options_key) {
      return
    }

    const optionList = options[definition.options_key] || []
    const validValues = new Set(optionList.map(option => String(option.value)))
    const currentValue = nextFilters[definition.key]

    if (!Array.isArray(currentValue) || currentValue.length === 0) {
      return
    }

    const prunedValue = currentValue.filter(item =>
      validValues.has(String(typeof item === "object" ? item.value : item))
    )

    if (prunedValue.length !== currentValue.length) {
      changed = true
      if (prunedValue.length > 0) {
        nextFilters[definition.key] = prunedValue
      } else {
        delete nextFilters[definition.key]
      }
    }
  })

  return changed ? nextFilters : filters
}

export const hasActiveFilterValue = value => {
  if (value === null || value === undefined || value === "") {
    return false
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  return true
}
